import Select, { components } from "react-select";

import PropTypes from "prop-types";
import React from "react";

// Menu option: selected rows get a light-green background and a trailing tick
// (in the text colour) instead of being pulled out as chips. Selected options
// stay listed (hideSelectedOptions={false}). An option flagged `divider` is ruled off from
// the ones below it.
export const CheckOption = (props) => (
  <components.Option
    {...props}
    className={props.data.divider ? "cf-rs__option--divider" : undefined}
  >
    <span className="cf-rs__optlabel">{props.label}</span>
    {props.isSelected && (
      <i className="fa-solid fa-check cf-rs__tick" aria-hidden="true" />
    )}
  </components.Option>
);

// Keep the control a fixed size: instead of piling selected values as chips,
// show a compact "N selected" summary (the search input is preserved).
export const CountValueContainer = ({ children, ...props }) => {
  const count = props.getValue().length;
  // children = [values/placeholder, Input]; keep only the trailing Input.
  const input = React.Children.toArray(children).pop();
  return (
    <components.ValueContainer {...props}>
      {count > 0 ? (
        <span className="cf-rs__summary">{count} selected</span>
      ) : (
        children
      )}
      {count > 0 && input}
    </components.ValueContainer>
  );
};

// Advanced filter bar: a search box, optional multi-select dropdown filters, and
// a result count.
// selects: [{ value: string[], onChange, label, allLabel, options: [{ value, label }] }]
//   - `value` is an array of selected option values (multi-select).
//   - `label` renders as a caption above the control.
//   - `allLabel` is the placeholder shown when nothing is selected.
export const FilterBar = ({
  query,
  onQuery,
  placeholder,
  selects,
  extra,
  count,
  countLabel,
  trailing,
  onClearAll,
  extraActive,
  compact,
}) => {
  // A filter counts as active when the search box holds text, a select holds a
  // value, or the page reports one of the filters it owns itself (a toggle in
  // `trailing`, say) through `extraActive`.
  const hasActiveFilter =
    Boolean(query) ||
    (selects ?? []).some((select) => (select.value ?? []).length > 0) ||
    Boolean(extraActive);

  // The dimension selects, minus the ones that have nothing to offer. A select
  // that already holds a value stays visible even then, or we would leave an
  // active filter with no way to remove it.
  const selectFields = (selects ?? [])
    .filter(
      (select) =>
        (select.options ?? []).length > 1 || (select.value ?? []).length > 0
    )
    .map((select, index) => (
      <label className="cf-field" key={index}>
        {/* The placeholder already names the dimension ("All countries"), so in
            compact mode the caption above it is pure repetition and costs a line
            of height per row. */}
        {select.label && !compact && (
          <span className="cf-field__label">{select.label}</span>
        )}
        <Select
          className="cf-rs"
          classNamePrefix="cf-rs"
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          backspaceRemovesValue={false}
          components={{
            Option: CheckOption,
            ValueContainer: CountValueContainer,
          }}
          options={select.options}
          value={select.options.filter((option) =>
            (select.value ?? []).includes(option.value)
          )}
          onChange={(picked) =>
            select.onChange(picked.map((option) => option.value))
          }
          placeholder={select.allLabel}
          aria-label={select.label ?? select.allLabel}
          noOptionsMessage={() => "No option"}
        />
      </label>
    ));

  // Sort, reset, count and the caller's own controls. Always present, unlike the
  // selects, whose number changes with the data.
  const controls = (
    <>
      {extra}

      {onClearAll && hasActiveFilter && (
        <button
          type="button"
          className="cf-filters__clear"
          onClick={onClearAll}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
          Clear all filters
        </button>
      )}

      <span className="cf-filters__spacer" />

      {count != null && (
        <span className="cf-filters__count">
          <strong>{count}</strong> {countLabel}
          {count === 1 ? "" : "s"}
        </span>
      )}

      {trailing}
    </>
  );

  const search = (
    <div className="cf-search">
      <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(event) => onQuery(event.target.value)}
        aria-label={placeholder}
      />
      {query && (
        <button
          type="button"
          className="cf-search__clear"
          onClick={() => onQuery("")}
          aria-label="Clear search"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );

  // Compact bars put the permanent controls on the first line and the selects
  // below, so a varying number of filters never shuffles the sort, the reset or
  // the view toggle around. A full-width zero-height spacer forces the break.
  return (
    <div className={"cf-filters" + (compact ? " cf-filters--compact" : "")}>
      {compact ? (
        <>
          {search}
          {controls}
          <span className="cf-filters__break" />
          {selectFields}
        </>
      ) : (
        <>
          {search}
          {selectFields}
          {controls}
        </>
      )}
    </div>
  );
};

FilterBar.propTypes = {
  query: PropTypes.string.isRequired,
  onQuery: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  selects: PropTypes.array,
  extra: PropTypes.node,
  count: PropTypes.number,
  countLabel: PropTypes.string,
  trailing: PropTypes.node,
  onClearAll: PropTypes.func,
  extraActive: PropTypes.bool,
  compact: PropTypes.bool,
};
